import axios from 'axios'
import type {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

export const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL, // Ensure this env var has a trailing slash or handle it consistently
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
})

// Request Interceptor
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken')
    if (token && config.headers && !config.url?.includes('token/refresh')) {
        config.headers.Authorization = `Bearer ${token}`
    }
    return config
})

// Response Interceptor & Refresh Logic
let isRefreshing = false

type FailedRequest = {
    resolve: (token: string) => void
    reject: (err: AxiosError) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token!)
        }
    })
    failedQueue = []
}

api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true
            const refreshToken = localStorage.getItem('refreshToken')

            if (!refreshToken) {
                const authStore = useAuthStore()
                authStore.clearAuth()
                return Promise.reject(error)
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers = {
                                ...originalRequest.headers,
                                Authorization: `Bearer ${token}`,
                            }
                            resolve(api(originalRequest))
                        },
                        reject: (err: AxiosError) => reject(err),
                    })
                })
            }

            isRefreshing = true

            try {
                // Use a clean axios instance for refresh to avoid interceptor loops
                const response = await axios.post(`${import.meta.env.VITE_API_URL}token/refresh/`, {
                    refresh: refreshToken,
                })

                const newAccessToken = response.data.access
                // Some backends might rotate refresh token too, check if present
                const newRefreshToken = response.data.refresh

                localStorage.setItem('accessToken', newAccessToken)
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', newRefreshToken)
                }

                api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
                processQueue(null, newAccessToken)

                originalRequest.headers = {
                    ...originalRequest.headers,
                    Authorization: `Bearer ${newAccessToken}`,
                }

                return api(originalRequest)
            } catch (refreshError) {
                processQueue(refreshError as AxiosError, null)
                const authStore = useAuthStore()
                authStore.clearAuth()
                return Promise.reject(refreshError)
            } finally {
                isRefreshing = false
            }
        }

        return Promise.reject(error)
    },
)
